import { useEffect } from "react";

// See PrivacyPage.jsx for why this legal body text stays English-only
// regardless of the active language prefix.
const TermsPage = () => {
  useEffect(() => {
    document.title = "Terms of Service | M A Galaxy International";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "The terms governing use of the M A Galaxy International website, Trade Portal, and enquiry forms."
      );
    }
  }, []);

  return (
    <div data-testid="terms-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-navy text-white">
        <div className="container-custom max-w-3xl">
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Legal</span>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold mt-4 mb-4">Terms of Service</h1>
          <p className="text-slate-400 text-sm">Last updated: 7 September 2026</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-custom max-w-3xl">
          <h2 className="font-outfit text-2xl font-bold text-navy mt-0 mb-4">1. Acceptance of These Terms</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            By accessing or using this website (magalaxyintl.com), operated by M A Galaxy Int UK Ltd (Company
            Registration Number 13671542) and M A Galaxy International (Korea HQ), you agree to these Terms of
            Service. If you do not agree, please do not use this website.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">2. About This Website</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website provides information about M A Galaxy International's solar panel trading business,
            including our services for selling and buying used, surplus, and new solar panels. The website allows
            visitors to submit enquiries, request access to our Trade Portal, and browse information about our
            operations, compliance, and completed projects.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website is a marketing and lead-generation tool.{" "}
            <strong className="text-navy">
              Submitting an enquiry, offer, or form through this website does not, by itself, create a binding
              contract between you and M A Galaxy International.
            </strong>{" "}
            Any actual sale, purchase, or trade of solar panels is subject to a separate, formal agreement between
            the parties.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">3. Use of This Website</h2>
          <p className="text-slate-600 leading-relaxed mb-4">You agree to use this website only for lawful purposes. You must not:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>Submit false, misleading, or fraudulent information through any form on this site</li>
            <li>Attempt to gain unauthorised access to the Admin panel, Trade Portal, or any other restricted area</li>
            <li>Use the website in any way that could damage, disable, or impair its functioning</li>
            <li>Scrape, copy, or reproduce content from this website for commercial purposes without our written permission</li>
          </ul>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">4. Trade Portal</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            Access to our Trade Portal is granted at our discretion via a personal access code. By using the Trade
            Portal, you agree that:
          </p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>Access codes are personal to you and should not be shared</li>
            <li>Offers submitted through the Trade Portal are private and sealed — you will not see other buyers' offers, and they will not see yours</li>
            <li>Submitting an offer does not guarantee acceptance; we may accept, reject, or negotiate any offer at our discretion</li>
            <li>Any resulting sale is subject to separate formal terms agreed between the parties</li>
          </ul>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">5. Accuracy of Information</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We aim to keep the information on this website — including panel specifications, project details, and
            pricing indications — accurate and up to date. However, brand availability, wattage, condition, and stock
            levels change frequently, and we do not guarantee that all information on this site is current at all
            times. Please contact us directly to confirm current availability and pricing before relying on any
            information for a business decision.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">6. Intellectual Property</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            All content on this website — including text, images, logos, and design — is owned by or licensed to
            M A Galaxy International, unless otherwise stated. You may not reproduce, distribute, or use this content
            for commercial purposes without our prior written consent.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">7. Third-Party Links</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website contains links to third-party platforms, including WhatsApp, LinkedIn, eBay, and Facebook
            Marketplace. We are not responsible for the content, policies, or practices of these third-party sites.
            Use of any linked site is at your own discretion and subject to that site's own terms.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">8. Disclaimer of Warranties</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website and its content are provided "as is" without warranties of any kind, whether express or
            implied, including but not limited to warranties of accuracy, completeness, or fitness for a particular
            purpose.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            To the fullest extent permitted by law, M A Galaxy International will not be liable for any indirect,
            incidental, or consequential loss arising from your use of this website. Nothing in these Terms excludes
            or limits our liability for death or personal injury caused by negligence, fraud, or any other liability
            that cannot be excluded under UK law.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">10. Governing Law</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            These Terms are governed by the laws of England and Wales. Any disputes arising from your use of this
            website will be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">11. Changes to These Terms</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We may update these Terms from time to time. The "Last updated" date above will reflect the most recent
            revision. Continued use of the website after changes are posted constitutes acceptance of the updated
            Terms.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">12. Contact Us</h2>
          <address className="not-italic text-slate-600 leading-relaxed">
            <strong className="text-navy">M A Galaxy Int UK Ltd</strong><br />
            62 New Road, Newhall, Swadlincote, Derby, DE11 0TH, United Kingdom<br />
            Email: <a href="mailto:info@magalaxyintl.com" className="text-gold hover:underline">info@magalaxyintl.com</a><br />
            Phone: <a href="tel:+447379339449" className="text-gold hover:underline"><span dir="ltr">+44 7379 339 449</span></a>
          </address>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
